'use client';

import { useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { HStack, VStack } from '@terra/ui-styled-system';
import { Add01Icon } from '@terra/ui-icons';
import { Button } from '../../../components/button';
import { Dialog } from '../../../components/dialog';
import { BodyDefault } from '../../../components/typography';
import { DocumentationUrlInputRow } from './DocumentationUrlInputRow';

type ApplicationDocumentationLinksDialogProps = {
  open: boolean;
  urls: string[];
  canAddLink: boolean;
  canConfirm: boolean;
  isSaving: boolean;
  onDismiss: () => void;
  onUrlChange: ({ index, value }: { index: number; value: string }) => void;
  onAddUrl: () => void;
  onRemoveUrl: ({ index }: { index: number }) => void;
  onSave: () => void | Promise<void>;
};

export const ApplicationDocumentationLinksDialog = ({
  open,
  urls,
  canAddLink,
  canConfirm,
  isSaving,
  onDismiss,
  onUrlChange,
  onAddUrl,
  onRemoveUrl,
  onSave,
}: ApplicationDocumentationLinksDialogProps) => {
  const t = useTranslations(
    'designSystem.documentsReports.applicationDocumentation',
  );
  const tCommon = useTranslations('designSystem.common');
  const handleOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (!nextOpen && !isSaving) {
        onDismiss();
      }
    },
    [isSaving, onDismiss],
  );

  return (
    <Dialog
      open={open}
      onOpenChange={handleOpenChange}
      size="sm"
      title={t('addLinksDialog.title')}
      description={t('addLinksDialog.description')}
      secondaryAction={{
        label: tCommon('cancel'),
        onClick: onDismiss,
        disabled: isSaving,
      }}
      primaryAction={{
        label: t('addFilesDialog.confirm'),
        onClick: onSave,
        disabled: !canConfirm,
        loading: isSaving,
      }}
    >
      <VStack alignItems="stretch" gap={2} w="full">
        {urls.map((url, index) => (
          <DocumentationUrlInputRow
            key={`dialog-documentation-url-${index}`}
            url={url}
            index={index}
            placeholder={t('documentationUrlPlaceholder')}
            invalidUrlMessage={t('addLinksDialog.invalidUrl')}
            removeAriaLabel={t('removeDocumentationUrl')}
            onChange={onUrlChange}
            onRemove={onRemoveUrl}
          />
        ))}

        <Button
          variant="ghost"
          size="sm"
          fontWeight="normal"
          alignSelf="flex-start"
          w="fit-content"
          disabled={!canAddLink || isSaving}
          onClick={onAddUrl}
        >
          <HStack gap={2} alignItems="center">
            <Add01Icon size={16} />
            <BodyDefault>{t('addLinksDialog.addLink')}</BodyDefault>
          </HStack>
        </Button>
      </VStack>
    </Dialog>
  );
};
